<template>
  <div class="main-content-wrapper">
    <div class="flex">
      <h1>Admin page</h1>
      <button class="button-primary" @click="refreshCache">
        Refresh cache
      </button>
    </div>
    <h3>
      <strong>Logged as</strong>: {{ site.site.companyName }} /
      <a :href="site.site.websiteUrl" target="_blank">{{
        site.site.websiteUrl
      }}</a>
      /
      {{ site.site._id }}
    </h3>
    <div class="search-wrapper">
      <div class="search-bar">
        <input
          v-model="typedInput"
          placeholder="Search"
          type="text"
          @keyup.enter="getSearchResult()"
        />
      </div>
      <div class="buttons">
        <button
          v-for="(field, index) in searchFields"
          :key="index"
          :class="{ searchField: true, active: index === isActive }"
          @click="selectedField(index)"
        >
          {{ field }}
        </button>
      </div>
      <div>
        <button class="search-button" @click="getSearchResult()">Search</button>
      </div>
    </div>
    <div class="grid-header">
      <p></p>
      <p>Last logged in accounts:</p>
      <p>Plan</p>
      <p>Views 30 days</p>
      <p>Videos</p>
      <p>Last activity</p>
      <p>User emails</p>
    </div>

    <div class="result">
      <div
        v-for="(result, index) in searchResult"
        :key="index"
        :class="['search-result', { 'is-even': index % 2 === 1 }]"
      >
        <button @click="logAs(index)"><LogAsIcon /></button>
        <div class="general-company-info">
          <p>{{ result.site.websiteUrl }}</p>
          <p class="light">{{ result.site.companyName }}</p>
          <p class="light">{{ result.site._id }}</p>
        </div>
        <div class="plan">
          <p>{{ getPlanCost(result.plan) }}</p>
          <p :class="getPlanLockClass(result.plan)">
            {{ getPlanLockLabel(result.plan) }}
          </p>
        </div>
        <div class="used-limits">
          <p>{{ result.last30DaysVideoPlays }}</p>
          <p class="limit-link" @click="openPlanEditor(index)">
            <span>Limit:</span> {{ getPlanViewLimit(result.plan) }}
          </p>
        </div>
        <div class="used-limits">
          <p>{{ result.amountOfVideos }}</p>
          <p class="limit-link" @click="openPlanEditor(index)">
            <span>Limit:</span> {{ getPlanVideosLimit(result.plan) }}
          </p>
        </div>
        <p>{{ result.lastLoginDate || "N/A" }}</p>
        <div class="user-emails">
          <p v-for="(mail, index) in result.usersEmails" :key="index">
            {{ mail }}
          </p>
        </div>
      </div>
    </div>
    <JsonEditModal
      v-if="isPlanModalOpen"
      :plan="editingPlan || {}"
      :account="editingCompany"
      @close="isPlanModalOpen = false"
      @save="savePlanJson"
    />
    <spinner-loader v-if="isLoading" />
  </div>
</template>

<script>
import api from "../api";
import { mapState } from "vuex";
import LogAsIcon from "@/assets/svg/log-as-icon.svg?inline";
import JsonEditModal from "@/components/JsonEditModal.vue";
import SpinnerLoader from "@/components/shared/SpinnerLoader.vue";

export default {
  components: {
    LogAsIcon,
    JsonEditModal,
    SpinnerLoader,
  },
  data() {
    return {
      searchFields: ["URL", "Site id", "Company name"],
      isActive: 0,
      typedInput: "",
      searchInput: "URL",
      searchResult: [],
      loggedAs: "",
      lastLoggedAccounts: [],
      showingDefault: true,
      isPlanModalOpen: false,
      editingPlan: null,
      editingIndex: -1,
      editingCompany: "",
      isLoading: false,
    };
  },

  computed: {
    ...mapState(["site", "user"]),
  },

  async created() {
    this.isLoading = true;
    try {
      await this.$store.dispatch("site/getSite", {
        siteId: this.$store.state.account.user.siteId,
      });
      await this.$store.dispatch("user/getUser", {
        userId: this.$store.state.account.user._id,
      });
      if (!this.user.user.isAdmin) {
        this.$router.push("dashboard");
      }
      this.$store.dispatch("site/saveAgencyId", "");

      await this.$store.dispatch("user/fetchLastLogAsAccounts", {
        userId: this.$store.state.account.user._id,
      });
      const real = this.$store.state.user.lastLogAsAccounts;
      this.lastLoggedAccounts =
        Array.isArray(real) && real.length
          ? real
          : Array.isArray(accounts)
          ? accounts.slice(0, 10)
          : [];
      this.searchResult = this.lastLoggedAccounts;
    } finally {
      this.isLoading = false;
    }
  },
  methods: {
    async refreshCache() {
      try {
        await api.refreshCache(this.site.site._id);

        this.$notify({
          title: this.$t("shared.toastMessage.cacheRefreshed"),
          type: "success",
        });
      } catch (err) {
        this.$notify({
          title: this.$t("shared.toastMessage.cacheError"),
          type: "error",
        });
        throw err;
      }
    },
    selectedField(index) {
      this.isActive = index;
      this.searchInput = this.searchFields[index];
    },

    getSearchResult() {
      const input = this.typedInput?.trim();

      if (!input) {
        this.searchResult = this.lastLoggedAccounts;
        this.showingDefault = true;
        return;
      }

      const field = this.searchInput || "URL";
      let query;
      if (field === "Site id") query = { siteId: input };
      else if (field === "URL") query = { websiteUrl: input };
      else if (field === "Company name") query = { companyName: input };

      this.showingDefault = false;
      this.retrieveSite(query);
    },
    async retrieveSite(query) {
      this.isLoading = true;
      try {
        const result = await api.adminSearch(query);
        this.searchResult = result;
        return result;
      } catch (err) {
        console.log(err);
      } finally {
        this.isLoading = false;
      }
    },
    async logAs(index) {
      const account = this.searchResult[index];
      const resultId = account?.site?._id || account?._id;
      if (!resultId) return;

      await this.$store.dispatch("account/logAs", { siteId: resultId });
      await this.$store.dispatch("site/getSite", {
        siteId: this.$store.state.account.user.siteId,
      });
      this.$store.dispatch("site/isLoggedAs", true);

      await this.$store.dispatch("user/addLastLogAsAccountFull", {
        userId: this.$store.state.account.user._id,
        account,
      });

      this.$notify({
        title: this.$t("shared.toastMessage.loggedAs"),
        type: "success",
      });
    },

    async logOut() {
      try {
        const originalUser = await api.getUser(
          this.$store.state.account.user._id
        );

        await this.$store.dispatch("account/logAs", {
          siteId: originalUser.siteId,
        });

        await this.$store.dispatch("site/getSite", {
          siteId: this.$store.state.account.user.siteId,
        });
        this.$store.dispatch("site/isLoggedAs", false);

        this.$notify({
          title: this.$t("shared.toastMessage.loggedOut"),
          type: "success",
        });
      } catch (err) {
        console.log(err);
      }
    },
    getPlanCost(plan) {
      const isTrial = this.getPricingField(plan, "isTrial");
      if (isTrial === true || isTrial === "true" || isTrial === 1) {
        return "Free trial";
      }

      const raw = this.getPricingField(plan, "cost");

      if (raw == null || raw === "") return "-";

      const currency = (plan?.paymentCurrency || "USD").toUpperCase();
      return this.formatAmount(raw, currency);
    },
    formatAmount(amount, currency) {
      const symbol = currency === "EUR" ? "€" : "$";
      const formatted = Number(amount).toLocaleString(undefined, {
        maximumFractionDigits: 2,
      });
      return `${symbol}${formatted}`;
    },
    getPlanLockLabel(plan) {
      if (plan?.isLocked === true) return "Locked";
      if (plan?.isLocked === false) return "Active";
      return "N/A";
    },

    getPlanLockClass(plan) {
      if (plan?.isLocked === true) return "locked";
      if (plan?.isLocked === false) return "active";
      return "";
    },
    getPlanViewLimit(plan) {
      const val = this.getPricingField(plan, "videoViewLimit");
      return this.formatLimit(val);
    },

    getPlanVideosLimit(plan) {
      const val = this.getPricingField(plan, "videosInCampaignsLimit");
      return this.formatLimit(val);
    },

    getPricingField(plan, field) {
      if (!plan || !plan.currentPlan || !Array.isArray(plan.pricings))
        return null;
      const pricing = plan.pricings.find((p) => p?.name === plan.currentPlan);
      return pricing ? pricing[field] : null;
    },

    formatLimit(val) {
      if (val === null || val === undefined || val === "") return "N/A";
      if (typeof val === "number") return val.toLocaleString();
      const n = Number(val);
      return Number.isFinite(n) ? n.toLocaleString() : "N/A";
    },
    openPlanEditor(index) {
      const row = this.searchResult[index];
      this.editingIndex = index;
      this.editingPlan = row?.plan || {};
      this.editingCompany =
        row?.site?.websiteUrl || row?.site?.companyName || "account";
      this.isPlanModalOpen = true;
    },

    async savePlanJson(nextPlanDoc) {
      try {
        const planId = this.editingPlan?._id;
        if (!planId) throw new Error("Missing plan._id");

        const updated = await this.$store.dispatch("plan/adminUpdatePlan", {
          planId,
          planDoc: nextPlanDoc,
        });

        if (this.editingIndex > -1) {
          const row = { ...this.searchResult[this.editingIndex] };
          row.plan = updated;
          this.$set(this.searchResult, this.editingIndex, row);

          const siteId = row?.site?._id || row?._id;
          const i = this.lastLoggedAccounts.findIndex(
            (a) => (a?.site?._id || a?._id) === siteId
          );
          if (i !== -1) {
            const cached = { ...this.lastLoggedAccounts[i], plan: updated };
            this.$set(this.lastLoggedAccounts, i, cached);
          }
        }

        this.$notify({
          title: "Plan updated",
          type: "success",
        });
      } catch (err) {
        this.$notify({
          title: err?.message || "Error updating plan",
          type: "error",
        });
        throw err;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.search-wrapper {
  display: flex;
  align-items: center;
  gap: 16px;
}
.buttons {
  display: flex;
  margin-bottom: 32px;
  margin-top: 32px;

  .searchField {
    @include base-font;
    @include font-smaller;
    color: #a3a3a3;
    background-color: white;
    border: 1px solid $dark-purple;
    padding: 8px 16px;

    &.active {
      color: white;
      background-color: $dark-purple;
    }

    &:first-child {
      border-top-left-radius: 2px;
      border-bottom-left-radius: 2px;
      border-right: none;
    }

    &:nth-child(2) {
      border-radius: 0;
    }

    &:last-child {
      border-top-right-radius: 2px;
      border-bottom-right-radius: 2px;
      border-left: none;
    }
  }
}

.search-bar {
  display: flex;
  justify-content: space-evenly;
  input {
    @include base-font;
    height: 31.6px;
    align-self: center;
    width: 30vw;
    @include input-style;
    padding-left: 8px;
    &::placeholder {
      color: #c4c4c4;
    }
  }
}
.search-button {
  @include base-font;
  @include font-smaller;
  color: white;
  background-color: $dark-purple;
  border: 1px solid $dark-purple;
  padding: 8px 32px;
  border-radius: 2px;
}
.grid-header {
  display: grid;
  grid-template-columns: 0.3fr 1.5fr 0.5fr 0.7fr 0.7fr 1fr 1fr;
  padding: 12px 8px;
  background-color: #ececec;
  border-bottom: 1px solid #d1d1d1;
  p {
    @include base-font;
    @include font-smaller;
    font-weight: 600;
  }
}
.result {
  margin-bottom: 64px;
}
.search-result {
  display: grid;
  grid-template-columns: 0.3fr 1.5fr 0.5fr 0.7fr 0.7fr 1fr 1fr;
  align-items: center;
  padding: 12px 8px;
  background-color: #f6f6f6;
  border-bottom: 1px solid #d1d1d1;
  &.is-even {
    background-color: #ffffff;
  }
  p {
    @include base-font;
    @include font-smaller;
    line-height: 16px;
  }
  button {
    background-color: transparent;
  }
}
.general-company-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  .light {
    color: #a3a3a3;
  }
}
.limit-link {
  cursor: pointer;
  span {
    text-decoration: underline;
  }
}
.plan {
  display: flex;
  flex-direction: column;
  gap: 8px;
  p {
    @include base-font;
    @include font-smaller;
  }
  .locked,
  .active {
    display: inline-flex;
    align-self: flex-start;
    padding: 4px 12px;
    border-radius: 20px;
  }
  .locked {
    background-color: #ffb4b5;
  }
  .active {
    background-color: #d4f8d3;
  }
}
.used-limits {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.user-emails {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.flex {
  display: flex;
  justify-content: space-between;
  margin-bottom: 16px;
}
</style>
